import type { EventDescription } from './types/event'
import { parseEventDescriptionToAlgoliaEventItem } from '~/utils/helpers'

const LAST_VIEWED_EVENTS_LOCAL_KEY = 'last-viewed-event-ids'
const LAST_VIEWED_EVENS_TIME_LOCAL_STORAGE_KEY = 'last-viewed-events-time'

const getLastViewedStorageKey = () => `${LAST_VIEWED_EVENTS_LOCAL_KEY}`

const safeJsonParse = (str: string | null, defaultReturn: any[] = []) => {
  let arr = defaultReturn || []
  if (!str) return arr
  try {
    arr = JSON.parse(str)
  } catch (_err) {
    // eslint-disable-next-line no-console
    console.warn('JSON.parse error', _err)
  }
  return arr
}

export const useLastViewedEvents = (loadImmediately = false): any => {
  const lastViewedEvents = ref<EventDescription[]>([])
  const parsedLastViewedEvents = ref<any[]>([])

  const getLastViewedEventsFromLocalStorage = async () => {
    try {
      if (!window?.localStorage) {
        // eslint-disable-next-line no-console
        console.warn('localStorage not available')
        return
      }

      const storageLastViewedEventsTime = window.localStorage.getItem(
        LAST_VIEWED_EVENS_TIME_LOCAL_STORAGE_KEY
      )
      const storageLastViewedEventsIds = safeJsonParse(
        window.localStorage.getItem(getLastViewedStorageKey())
      )

      const now = Date.now()
      const thirtyMin = 1000 * 60 * 30
      const isPastThirtyMin =
        thirtyMin < now - Number(storageLastViewedEventsTime)

      if (
        !storageLastViewedEventsIds ||
        storageLastViewedEventsIds.length === 0
      ) {
        // eslint-disable-next-line no-console
        console.log('No last viewed events found in localStorage')
        return
      }

      parsedLastViewedEvents.value = []

      const events = await loadEventsByIds(storageLastViewedEventsIds)

      // check if events exists and has data
      if (!events || !events.data) {
        // eslint-disable-next-line no-console
        console.warn('No events data received from API')
        return
      }

      ;(events.data || [])
        .map((ev: any) => {
          ev.__order = storageLastViewedEventsIds.indexOf(ev.id) + 1
          return ev
        })
        .filter((ev: any) => ev.__order) // setting up for ordering
        .sort((ae: any, be: any) => ae.__order - be.__order) // actual ordering
        .map((ev: any) =>
          parsedLastViewedEvents.value.push(
            parseEventDescriptionToAlgoliaEventItem(ev)
          )
        )

      // Checks if the last viewed events are past 30 minutes
      // Only diplay last viewed if the first stored event id is older than 30 minutes.
      if (isPastThirtyMin) {
        lastViewedEvents.value = parsedLastViewedEvents.value
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in getLastViewedEventsFromLocalStorage:', error)
      // in case of error, just don't show the last viewed events
      // but don't break the whole component
    }
  }

  const loading = ref(false)

  /**
   * Manually trigger loading of last viewed events
   * Useful for lazy loading when needed (e.g., when search dropdown opens)
   */
  const loadLastViewedEvents = async () => {
    if (loading.value) return // Prevent duplicate requests
    loading.value = true
    try {
      await getLastViewedEventsFromLocalStorage()
    } finally {
      loading.value = false
    }
  }

  const loadEventsByIds = async (
    eventIDs: string[],
    retryCount = 0
  ): Promise<{ data: any[] }> => {
    if (eventIDs?.length < 1) return { data: [] }

    const params = `?include=supplier,categories&search=hasEvent:false;id:${eventIDs.join(
      ','
    )}`

    const maxRetries = 2
    const retryDelay = 1000 // 1 second

    try {
      const eventsData = await kftApiRequest(`v1/store/events/${params}`, {
        method: 'GET',
      })
      return {
        data: Array.isArray((eventsData as any)?.data)
          ? (eventsData as any).data
          : [],
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(
        `loading last seen events failed (attempt ${retryCount + 1}/${maxRetries + 1})`,
        e
      )

      // if this is not the last attempt, retry the request
      if (retryCount < maxRetries) {
        // eslint-disable-next-line no-console
        console.log(`Retrying in ${retryDelay}ms...`)
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
        return loadEventsByIds(eventIDs, retryCount + 1)
      }

      // after all attempts, return empty result
      // eslint-disable-next-line no-console
      console.warn(
        'All retry attempts failed for loading last seen events, returning empty data'
      )
      return { data: [] }
    }
  }

  onMounted(() => {
    if (loadImmediately) {
      loadLastViewedEvents()
    }
  })

  return {
    lastViewedEvents: computed(() => lastViewedEvents.value),
    loadLastViewedEvents,
    loading: computed(() => loading.value),
  }
}
